import { Box, Button, Typography } from '@mui/material';
import SectionTitle from 'components/ui/typography/SectionTitle';
import dayjs from 'dayjs';
import { useId } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const FORM_DENSITY_PRESETS = {
  compact: 160, // Per form densi, dati brevi, cifre monetarie e codici
  standard: 240,
  wide: 320 // Per form con campi testuali estesi, descrizioni o indirizzi lunghi
} as const;

export type FormDensity = keyof typeof FORM_DENSITY_PRESETS;

interface EnhancedFormProps {
  /** Callback triggered when the form is successfully submitted. Receives form values. */
  onSubmit:
    | any
    | ((
        values: any,
        form: any,
        callback?: () => void
      ) => object | Array<object> | void | Promise<object | Array<object>>);
  /** Optional cancellation callback. Defaults to navigating back one level if not provided. */
  onCancel?: any | (() => void);
  /** Initial values to populate the form fields on mount. */
  initialValues?: any | object;
  /** Array of form-level decorator functions (e.g., validation listeners or field modifiers). */
  decorators?: Array<any>;
  /** Adjusts button labels to 'Save' if true, or 'Insert/Create' if false. */
  edit?: boolean;
  /** Form content, fields, and layouts to be rendered inside the form context. */
  children?: any | React.ReactNode;
  /** Hides the standard bottom action controls (Back, Save, Insert) when set to true. */
  hideControls?: boolean;
  costumControls?: React.ReactNode;
  submitLabel?: string;
  hideBackResetButton?: boolean;
  /** HTML id attribute for the form element. Automatically falls back to a timestamped string if omitted. */
  formId?: any | string;
  /** Disables and completely hides the submit execution button if set to true. */
  disableEdit?: boolean;

  /** Optional title displayed at the top of the form layout. */
  title?: React.ReactNode;
  /** Optional subtitle text displayed right below the main title. */
  subtitle?: React.ReactNode;
  /** Optional css configuration for the main title. */
  classNameTitle?: string;

  // ---- LAYOUT ENGINE CONFIGURATION PROPERTIES ----
  autoStyleLayout?: boolean;
  /**
   * Defines the programmatic layout distribution style for the child inputs.
   * - 'stack': Arranges components vertically in a single uniform column.
   * - 'inline': Flow components horizontally in a flexible wrapped row layout.
   * - 'grid': Activates the fully dimensional CSS Grid layout matrix.
   * @default 'grid'
   */
  layout?: 'stack' | 'grid' | 'inline';

  /**
   * The structural spacing multiplier mapped directly to native Material-UI theme tokens.
   * For example, a value of 2 equals 16px, and 3 equals 24px of uniform margin gap between elements.
   * @default 2.5 (20px)
   */
  gap?: number;

  /**
   * MUTUALLY EXCLUSIVE WITH 'columns' (Only evaluates when 'layout' is 'grid')
   *
   * Governs the responsive, fluid "auto-fit" column track footprint. The layout engine
   * automatically crams as many fields into a single horizontal row as physically possible
   * without letting any input shrink below the selected design token threshold:
   * - 'compact': Core narrow track base (~160px), optimal for currency, toggles, or brief numeric entries.
   * - 'standard': Balanced enterprise distribution track base (~240px).
   * - 'wide': Large extended field tracks (~320px), ideal for text blocks and large descriptions.
   *
   * Note: This property is completely IGNORED if a 'columns' prop is explicitly supplied.
   * @default 'standard'
   */
  density?: FormDensity;

  /**
   * MUTUALLY EXCLUSIVE WITH 'density' (Only evaluates when 'layout' is 'grid')
   *
   * Forces a rigid, geometric layout grid over the viewport instead of the fluid auto-fitting engine.
   * Acceptable entry structures:
   * - 'number': Generates a mathematically locked grid matrix (e.g., columns={12}). This empowers
   *             nested IdentityFields to leverage exact 'gridSpan' configurations for pixel-perfect asymetry.
   * - 'object': Maps varying strict column templates down to responsive Material-UI breakpoints
   *             (e.g., columns={{ xs: 1, sm: 6, md: 12 }}).
   *
   * Note: Providing this parameter immediately overrides and suppresses any active 'density' behavior.
   */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number };
}

/**
 * A customized layout abstraction wrapper around `react-final-form` designed for MUI 6 ecosystems.
 *
 * This component standardizes layout delivery, injects global form handling lifecycle logic,
 * unifies bottom pristine/submitting action triggers, and completely suppresses unlocalized
 * HTML5 browser validation alerts globally.
 *
 * ### Layout Operational Mechanics
 * The component operates under three core structural layout patterns configured via the `layout` prop:
 * 1. **`grid` (Default)**: Leverages a split layout paradigm. It switches dynamically between a mathematical,
 *    fixed-width multi-column framework (using `columns`), and an automated fluid-responsive wrapping scheme
 *    (using `density`).
 * 2. **`stack`**: Arranges children vertically in a uniform single-file stack using Flexbox.
 * 3. **`inline`**: Flows and wraps children across horizontal rows natively using Flexbox.
 *
 * ### Crucial Property Dependencies & Exclusivity (Under layout="grid")
 * * **Fixed Coordinate Templates**: Passing an explicit `number` or an `object` configuration to the `columns`
 *   parameter forces a mathematical grid track structure (e.g., `columns={12}`). This mode allows nested fields
 *   wrapped in `` to utilize concrete integer `gridSpan` numbers (e.g., `gridSpan={3}`) for exact,
 *   asymmetric desktop layout designs.
 * * **Fluid Flow Template**: Omitting the `columns` property entirely instructs the browser to deploy an automated
 *   `auto-fit` engine driven by the `density` property. Columns shrink and multiply inside rows in real-time based on
 *   the parent box container width.
 * * **Collision Protection**: If both `columns` and `density` are supplied simultaneously, **`columns` overrides
 *   and completely suppresses `density` handling**.
 *
 * @example
 * #### Pattern 1: Absolute Geometric Control (Perfect for your Anagrafica form)
 * ```tsx
 * <EnhancedForm
 *   title="Anagrafica di base"
 *   formId="baseRegistryForm"
 *   onSubmit={handleFormSubmit}
 *   layout="grid"
 *   columns={12} // Triggers rigid grid tracking; gridSpan configurations must equal a sum of 12 per row
 *   gap={2.5}
 * >
 *
 * </EnhancedForm>
 * ```
 *
 * @example
 * #### Pattern 2: Automated Structural Fluidity (Rapid form development)
 * ```tsx
 * <EnhancedForm
 *   title="Fatturazione Rapida"
 *   formId="billingFastForm"
 *   onSubmit={handleFormSubmit}
 *   layout="grid"
 *   density="compact" // Forces columns to pack tightly (~160px min-width) without explicit spans
 * >
 *
 * </EnhancedForm>
 * ```
 */
const EnhancedForm = ({
  onSubmit,
  submitLabel,
  onCancel,
  initialValues,
  decorators,
  edit,
  children,
  hideControls,
  costumControls,
  hideBackResetButton = true,
  formId,
  disableEdit,
  title,
  subtitle = 'Compila i campi sottostanti per salvare i dettagli della configurazione.',
  autoStyleLayout = false,
  layout = 'grid',
  columns,
  gap = 2,
  density = 'standard',
  classNameTitle = 'mb-6'
}: EnhancedFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const generatedFormId = useId();
  const resolvedFormId = formId ?? `form-${generatedFormId}`;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      decorators={decorators}
      render={({ handleSubmit, form, submitting, pristine, values, invalid, errors }) => {
        const handleCancel = () => {
          form.reset(); // Native react-final-form reset mechanism to revert all fields to initialValues

          if (onCancel) {
            onCancel();
          } else {
            navigate('..');
          }
        };

        /**
         * Calcola dinamicamente le proprietà CSS della griglia e dei contenitori dei campi.
         */
        const getLayoutStyles = () => {
          if (layout === 'grid') {
            // MODALITÀ GRID RIGIDA (Se columns è definito come numero, es. 12)
            if (typeof columns === 'number') {
              return {
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: gap,
                width: '100%'
              };
            }

            // MODALITÀ GRID RESPONSIVE OBJECT (Se columns è un oggetto MUI, es. { xs: 12, md: 6 })
            if (typeof columns === 'object') {
              return {
                display: 'grid',
                gap: gap,
                width: '100%',
                gridTemplateColumns: {
                  xs: columns.xs
                    ? `repeat(${columns.xs}, minmax(0, 1fr))`
                    : 'repeat(1, minmax(0, 1fr))',
                  sm: columns.sm ? `repeat(${columns.sm}, minmax(0, 1fr))` : undefined,
                  md: columns.md ? `repeat(${columns.md}, minmax(0, 1fr))` : undefined,
                  lg: columns.lg ? `repeat(${columns.lg}, minmax(0, 1fr))` : undefined
                }
              };
            }

            // MODALITÀ GRID FLUIDA AUTOMATICA
            const minWidthPx = FORM_DENSITY_PRESETS[density] || FORM_DENSITY_PRESETS.standard;
            return {
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${minWidthPx}px, 1fr))`,
              gap: gap,
              width: '100%'
            };
          }
          if (layout === 'inline') {
            return {
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: gap,
              alignItems: 'center'
            };
          }
          return {
            display: 'flex',
            flexDirection: 'column',
            gap: gap
          };
        };

        return (
          <form onSubmit={handleSubmit} id={resolvedFormId}>
            {title && (
              <div className={classNameTitle}>
                {title && <SectionTitle>{title}</SectionTitle>}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mt-1"
                  sx={{ lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                  {subtitle}
                </Typography>
              </div>
            )}

            <Box sx={autoStyleLayout ? getLayoutStyles() : {}}>{children}</Box>

            {!hideControls && (
              <>
                {costumControls ? (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                    {costumControls}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: hideBackResetButton ? 'flex-end' : 'space-between',
                      alignItems: 'center',
                      gap: 2
                    }}>
                    {!hideBackResetButton && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                        onClick={handleCancel}>
                        {onCancel ? t('actions.reset') : t('actions.back')}
                      </Button>
                    )}
                    {!disableEdit && (
                      <Button
                        form={resolvedFormId}
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          minWidth: '110px',
                          '&.Mui-disabled': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            opacity: 0.65
                          }
                        }}
                        disabled={submitting || pristine || invalid}>
                        {submitLabel || (edit ? t('actions.save') : t('actions.insert'))}
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </form>
        );
      }}
    />
  );
};

export default EnhancedForm;
